import { useEffect, useRef } from 'react';

/**
 * Seeds a form from the server's copy, field by field, and only where that copy
 * really changed.
 *
 * ⚠️ **Both editors need this, for the same reason**, which is why it lives
 * here rather than inside one of them. Pictures save outside the Save button —
 * the bytes travel in their own request, and holding them in a draft would
 * leave orphaned files behind — so every upload, removal and reorder writes the
 * row back into the query cache. Each write is a new object, so the obvious
 * effect seeds from it again and overwrites whatever somebody was typing:
 * uploading a cover while a paragraph was half-written threw the paragraph away
 * with nothing said.
 *
 * Per field rather than per form, which is the half a value comparison alone
 * still gets wrong: removing a cover *does* change something on the server —
 * it clears the alt text — so the whole draft was reloaded and the same
 * paragraph went again, through a different button. Only the fields the server
 * actually changed are overwritten; everything else keeps what somebody typed.
 *
 * `also` forces a full re-seed even when nothing changed, for the writes where
 * reloading is the point: the open language (two languages nobody has written
 * in yet hold identical drafts, and without it what was typed in the first
 * would sit under the second), and a save (the boxes are trimmed on the way
 * out, so a tidied one comes back reported as unchanged — see either editor).
 */
/*
 * `object` and not `Record<string, unknown>`: the drafts are interfaces, and an
 * interface has no index signature, so the narrower constraint rejects every
 * real caller. The cost is that `object` also admits `Map`, `Set` and `Date`,
 * whose `Object.keys` is empty — one of those would seed once and then never
 * update again, silently. Nothing passes one today; a plain object or an array
 * is what this handles.
 */
/*
 * ⚠️ A `saved` belonging to a different record must arrive in a different
 * component, not through this hook. Seeding is per field, so walking from one
 * app to another without a remount would *merge* them: every field the two
 * happen to share keeps what was typed into the first, and Save writes it onto
 * the second. `FreshPerId` in `App.tsx` is what keys the route by id and makes
 * that impossible — this hook depends on it and cannot check it.
 */
export function useSeeded<T extends object>(
  saved: T | null,
  apply: (update: (current: T | null) => T | null) => void,
  also = '',
): void {
  const seeded = useRef<{ also: string; value: T } | null>(null);

  useEffect(() => {
    if (!saved) return;

    const previous = seeded.current;
    seeded.current = { also, value: saved };

    // Nothing to merge with: the first load, or a different language.
    if (!previous || previous.also !== also) {
      apply(() => saved);
      return;
    }

    /*
     * ⚠️ A list is compared and replaced whole; an object field by field.
     *
     * Merging a list by index is wrong in the one way that matters: somebody
     * who added a release locally has shifted every index after it, so writing
     * the server's index 2 over theirs puts a different release's text into the
     * row they are typing in. There are no independent fields in a list —
     * position *is* the identity — so the honest granularity is all or nothing.
     *
     * Which costs nothing in the case this hook exists for: a screenshot upload
     * does not touch releases or plans, so the comparison says "unchanged" and
     * the draft is left alone. It only decides the rarer case, where somebody
     * else really did save a change to the list — and there the price is higher
     * than the per-field branch's, not the same: the whole list goes, including
     * the row being typed in and any row added locally that has no id yet.
     * `ReleaseDraft` and `PlanDraft` both carry `id?`, so merging by id rather
     * than by position is the way out if that ever happens to somebody.
     */
    if (Array.isArray(saved)) {
      if (JSON.stringify(previous.value) === JSON.stringify(saved)) return;
      apply(() => saved);
      return;
    }

    const changed = (Object.keys(saved) as (keyof T)[]).filter(
      (field) => JSON.stringify(previous.value[field]) !== JSON.stringify(saved[field]),
    );
    if (changed.length === 0) return;

    apply((current) => {
      // Knowingly unguarded, and unreachable as the two editors stand: this
      // branch only runs once a full seed has already put something in the
      // draft, and neither editor sets one back to null. Kept as the honest
      // answer to a null rather than a crash, for a third caller that might.
      if (!current) return saved;
      const next = { ...current };
      for (const field of changed) {
        /*
         * ⚠️ A field somebody has typed in since the last seed is theirs. The
         * server changing the *same* field while they type used to win here —
         * which nothing could do until "Translate with AI": it writes exactly
         * the Japanese tagline somebody may be halfway through, and QA watched
         * a draft turn into machine text with the Unsaved mark vanishing. What
         * was typed stays; Save then writes it over the translation, which is
         * the order the person would choose. Untouched fields still follow the
         * server, as before.
         */
        const typedSince = JSON.stringify(current[field]) !== JSON.stringify(previous.value[field]);
        if (!typedSince) next[field] = saved[field];
      }
      return next;
    });
    // `apply` is a setState function, stable for the life of the component.
    //
    // `also` is here for correctness rather than because it has ever fired
    // alone: every caller's `saved` is a memo that already lists what `also` is
    // built from, so the two change together. Dropping it breaks no test today
    // and would be a trap the day one of them stops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, also]);
}
